import { httpClient, sessionlessHttpClient } from '@/shared/api/httpClient'
import type { SecuritySessionResponse } from '@/shared/types/security'
import type { SessionGrant } from '@/modules/auth/utils/sessionHandoff'
import type { ProfileBaseInfoResponse } from '@/modules/profile/types/profile'
import { getCaptchaFieldName } from '@/shared/utils/captchaConfig'

/**
 * IGRA-386 — состояние доставки письма с кодом входа.
 * `delayed` — письмо задерживается (временный отказ или тишина провайдера),
 * `rejected` — почтовый сервер письмо отклонил, ждать бессмысленно.
 */
export type OtpDeliveryStatus = 'pending' | 'delivered' | 'delayed' | 'rejected' | 'confirmed'

/** Каналы вместо письма, порядок приоритета задаёт бэкенд. */
export type OtpDeliveryChannel = 'telegram' | 'phone' | 'email'

/** Выбор до отправки — не список аварийных каналов доставки. */
export type OtpChoiceChannel = 'telegram' | 'email'
export interface OtpChoice {
  channels: OtpChoiceChannel[]
  captcha_session?: string
}

export interface RecoveryCodeResponse {
  choose?: OtpChoice
  captchaSession?: string
  session?: string
  reciever?: OtpChoiceChannel
  reciever_address?: string
}

export interface OtpDeliveryStatusResponse {
  status: OtpDeliveryStatus
  /** Короткий код причины отказа от провайдера; человеку его не показываем. */
  reason?: string
  elapsed_seconds: number
  channels: OtpDeliveryChannel[]
  /**
   * IGRA-388/389 — вход подтверждён другим каналом: одноразовый ключ, который
   * эта же вкладка меняет на сессию существующим login-by-magic.
   */
  login_token?: string
  /** Каким каналом подтвердили: нужно только аналитике. */
  channel?: string
}

/** IGRA-388 — ссылка на бота вместо письма. */
export interface OtpTelegramLinkResponse {
  link: string
  expires_in: number
}

/** IGRA-390 — что мы знаем о почте пользователя. */
export type OtpDeliveryUserStatus = 'ok' | 'delayed' | 'rejected'

export interface EmailDeliveryStateResponse {
  status: OtpDeliveryUserStatus
  updated_at: string | null
  preferred_channel: OtpDeliveryChannel
}

/** IGRA-389 — состояние подтверждения звонком. */
export type OtpPhoneCallState =
  | 'none'
  | 'pending'
  | 'confirmed'
  | 'expired'
  | 'mismatch'
  | 'unavailable'

export interface OtpPhoneCallResponse {
  state: OtpPhoneCallState
  /** Номер, на который человек звонит сам. Есть только у успешного старта. */
  call_phone?: string
  expires_in?: number
}

export interface SaveContactsDto {
  login?: string
  first_name: string
  last_name: string
  phone: string
  telegram?: string
  sex?: number // 1 = Male, 2 = Female
}

export interface ChangePasswordDto {
  passwordold: string // current (old)
  passwordnew: string
  passwordretry: string
}

export interface ChangePayPassDto {
  passwordold: string
  passwordnew: string
  passwordretry: string
  confirmAction?: string
  confirmActionCode?: string
}

export interface PayoutSettingsDto {
  bep20_wallet?: string
  gonka_wallet?: string
  confirmAction?: string
  confirmActionCode?: string
}

export interface NotificationSettingsDto {
  mailing: number // Bitmask
  company_user_id?: number
}

/** IGRA-106 — единственная ошибка magic-входа: деталей бэкенд не раскрывает. */
export const MAGIC_LINK_INVALID = 'MAGIC_LINK_INVALID'

export class ProfileApi {
  /**
   * Get basic user info including wallet and access level
   */
  static async getBaseInfo(token?: string): Promise<ProfileBaseInfoResponse> {
    // Проверка сохранённой сессии не должна запускать глобальный logout
    // или принимать токен/cookie от уже устаревшего ответа.
    const response = token === undefined
      ? await httpClient.get<ProfileBaseInfoResponse>('/profile/base-info')
      : await sessionlessHttpClient.get<ProfileBaseInfoResponse>('/profile/base-info', {
        headers: { 'x-auth': token }, credentials: 'omit',
      })

    return response
  }

  /**
   * Initialize a security action session (triggers code sending)
   * @param actionName e.g. 'change_pay_pass'
   */
  static async initActionSession(actionName: string, payload: Record<string, unknown> & { channel?: OtpChoiceChannel } = {}): Promise<SecuritySessionResponse> {
    const formData = new FormData()
    formData.append('actionName', actionName)
    Object.entries(payload).forEach(([key, val]) => {
      formData.append(key, String(val))
    })

    // Using postRaw to access response headers (for email-code in dev mode)
    const { data } = await httpClient.postRaw<SecuritySessionResponse>('/security/init-action-session', formData)
    return data
  }

  /**
   * Update personal contacts
   */
  static async updateContacts(data: SaveContactsDto): Promise<{ result: string }> {
    const formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, String(value))
      }
    })

    // Using endpoint suggested by legacy placeholder
    return httpClient.post('/profile/edit-contacts', formData)
  }

  /**
   * Change Main Password
   */
  static async changePassword(data: ChangePasswordDto): Promise<{ result: string }> {
    const formData = new FormData()
    formData.append('passwordold', data.passwordold)
    formData.append('passwordnew', data.passwordnew)
    formData.append('passwordretry', data.passwordretry)

    // Using implied route from older codebase conventions or as discussed
    // 'account/security/edit-pass' seems standard for this project structure
    return httpClient.post('/security/edit-pass', formData)
  }

  /**
   * Change Payment Password (PayPass)
   */
  static async changePayPassword(data: ChangePayPassDto): Promise<{ result: string }> {
    const formData = new FormData()
    formData.append('passwordold', data.passwordold)
    formData.append('passwordnew', data.passwordnew)
    formData.append('passwordretry', data.passwordretry)
    if (data.confirmAction) formData.append('confirmAction', data.confirmAction)
    if (data.confirmActionCode) formData.append('confirmActionCode', data.confirmActionCode)

    return httpClient.post('/security/edit-pay-pass', formData)
  }

  /**
   * Update Payment Methods / Wallets
   */
  static async updatePaymentMethods(data: PayoutSettingsDto): Promise<{ result: string }> {
    const formData = new FormData()
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, String(value))
      }
    })

    return httpClient.post('/profile/edit-payments', formData)
  }

  /**
   * Upload Avatar
   */
  static async uploadAvatar(file: File): Promise<any> {
    const formData = new FormData()
    formData.append('uploadfile', file)

    return httpClient.post('/profile/upload/avatar', formData)
  }

  /**
   * Update Notification Settings
   */
  static async updateSettings(data: NotificationSettingsDto): Promise<{ result: string }> {
    // Legacy mapping: mailing[news], mailing[security]
    const formData = new FormData()

    if (data.mailing) {
      Object.entries(data.mailing).forEach(([key, val]) => {
        formData.append(`mailing[${key}]`, String(val))
      })
    }

    if (data.company_user_id) {
      formData.append('company_user_id', String(data.company_user_id))
    }

    return httpClient.post('/account/settings/edit-settings', formData)
  }

  /**
   * Send verification code to new email
   * Uses action: change_email_new_email
   */
  static async sendEmailVerificationCode(email: string): Promise<{ result: string }> {
    const formData = new FormData()
    formData.append('actionName', 'change_email_new_email')
    formData.append('email', email)

    // Using postRaw to access response headers (for email-code in dev mode)
    const { data } = await httpClient.postRaw<{ result: string }>('/security/init-action-session', formData)
    return data
  }

  /**
   * Change email with confirmation
   */
  static async changeEmail(data: {
    email: string;
    code: string;
    confirmAction?: string;
    confirmActionCode?: string;
  }): Promise<{ result: string }> {
    const formData = new FormData()
    formData.append('new_email', data.email)
    formData.append('code', data.code)

    // Security confirmation
    if (data.confirmAction) {
      formData.append('confirmAction', data.confirmAction)
    }
    if (data.confirmActionCode) {
      formData.append('confirmActionCode', data.confirmActionCode)
    }

    return httpClient.post('/security/edit-email', formData)
  }

  /**
   * Get mf_authorization (2FA) status
   */
  static async getMfAuthorization(): Promise<{ enabled: boolean; totp_enabled: boolean }> {
    return httpClient.get('/profile/settings/mf-authorization')
  }

  /**
   * Enable/disable mf_authorization (2FA via OTP/TOTP)
   * When disabling, confirmAction and confirmActionCode are required
   */
  static async setMfAuthorization(
    enabled: boolean,
    options?: { confirmAction?: string; confirmActionCode?: string }
  ): Promise<{ result: string; enabled: boolean }> {
    const formData = new FormData()
    formData.append('enabled', enabled ? '1' : '0')

    if (options?.confirmAction) {
      formData.append('confirmAction', options.confirmAction)
    }
    if (options?.confirmActionCode) {
      formData.append('confirmActionCode', options.confirmActionCode)
    }

    return httpClient.post('/profile/settings/mf-authorization', formData)
  }

  /**
   * Check if email exists and get migration status
   * Returns a captcha session token for subsequent requests (2min TTL)
   */
  static async checkEmail(email: string, recaptchaToken?: string): Promise<{
    exists: boolean
    isMigrated: boolean
    domain: string | null
    captchaSession: string
  }> {
    // Delete auth cookie before checking email (user is not authorized if calling this endpoint)
    document.cookie = 'dasdasdaddjdj=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'

    // Clear localStorage authToken to prevent x-auth header from being sent
    const storageKey = import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken_igra'
    localStorage.removeItem(storageKey)

    const payload: Record<string, string> = { email }
    if (recaptchaToken) {
      payload[getCaptchaFieldName(recaptchaToken)] = recaptchaToken
    }
    const response = await httpClient.post<{ data: { exists: boolean; isMigrated: boolean; domain: string | null; captchaSession: string } }>('/profile/check-email', payload)
    return response.data
  }

  /**
   * Send login code to user's email/telegram
   * Accepts either recaptchaToken or captchaSession
   */
  static async sendLoginCode(
    email: string,
    options?: { recaptchaToken?: string; captchaSession?: string; channel?: 'email' }
  ): Promise<{
    choose?: OtpChoice
    session?: string
    reciever?: string
    reciever_address?: string
    /**
     * IGRA-390 — письмо НЕ отправлено: человек уже входил другим каналом и
     * ждать отказа почты второй раз не должен. Сессия кода при этом заведена.
     */
    skipped_email?: boolean
    preferred_channel?: OtpDeliveryChannel
    channels?: OtpDeliveryChannel[]
  }> {
    const payload: Record<string, string> = { email }
    if (options?.captchaSession) {
      payload['captcha-session'] = options.captchaSession
    } else if (options?.recaptchaToken) {
      payload[getCaptchaFieldName(options.recaptchaToken)] = options.recaptchaToken
    }
    if (options?.channel) payload.channel = options.channel
    const response = await httpClient.post<{
      data: {
        choose?: OtpChoice
        session?: string
        reciever?: string
        reciever_address?: string
        skipped_email?: boolean
        preferred_channel?: OtpDeliveryChannel
        channels?: OtpDeliveryChannel[]
      }
    }>('/profile/login-by-code', payload)
    return response.data
  }

  /**
   * IGRA-386 — судьба письма с кодом: дошло, отклонено или задерживается.
   * Отвечает только тому клиенту, который код и запрашивал; для всех
   * остальных ответ неотличим от «письмо в пути».
   */
  /**
   * IGRA-388 — ссылка на бота для текущей сессии кода.
   *
   * Ссылку выдают только той вкладке, которая код и запрашивала; чужой адрес
   * и чужая сессия отвечают одной и той же ошибкой.
   */
  static async requestLoginCodeTelegram(email: string): Promise<OtpTelegramLinkResponse> {
    const response = await httpClient.post<{ data: OtpTelegramLinkResponse }>(
      '/profile/login-by-code/telegram',
      { email }
    )
    return response.data
  }

  /**
   * IGRA-389 — завести звонок. Номер и согласие проверяет бэкенд: за каждую
   * проверку сервису платят, поэтому чужой номер до него не доходит.
   */
  static async startPhoneVerification(
    email: string,
    phone: string,
    consent: boolean
  ): Promise<OtpPhoneCallResponse> {
    const response = await httpClient.post<{ data: OtpPhoneCallResponse }>(
      '/profile/login-by-code/phone/start',
      { email, phone, consent }
    )
    return response.data
  }

  /** IGRA-389 — судьба звонка. Вебхуков у сервиса нет, только опрос. */
  static async getPhoneVerificationStatus(email: string): Promise<OtpPhoneCallResponse> {
    const response = await httpClient.get<{ data: OtpPhoneCallResponse }>(
      `/profile/login-by-code/phone/status?email=${encodeURIComponent(email)}`
    )
    return response.data
  }

  /** IGRA-390 — состояние почты для кодов входа в настройках профиля. */
  static async getEmailDeliveryState(): Promise<EmailDeliveryStateResponse> {
    const response = await httpClient.get<{ data: EmailDeliveryStateResponse }>('/profile/email-delivery')
    return response.data
  }

  /**
   * IGRA-390 — «Проверить снова»: одно письмо с кодом. Статус меняет не эта
   * ручка, а событие вебхука ESP, поэтому ответ возвращает ПРЕЖНИЙ статус.
   */
  static async recheckEmailDelivery(): Promise<{ sent: boolean; status: OtpDeliveryUserStatus }> {
    const response = await httpClient.post<{ data: { sent: boolean; status: OtpDeliveryUserStatus } }>(
      '/profile/email-delivery/recheck',
      {}
    )
    return response.data
  }

  static async getLoginCodeStatus(email: string): Promise<OtpDeliveryStatusResponse> {
    const response = await httpClient.get<{ data: OtpDeliveryStatusResponse }>(
      `/profile/login-by-code/status?email=${encodeURIComponent(email)}`
    )
    return response.data
  }

  /**
   * Verify login code and authenticate user
   */
  static async verifyLoginCode(email: string, code: string, isCurrent?: () => boolean): Promise<{
    token: string
    location: string
    /** OTP-R6c: при включённом TOTP `token` — pending-ключ, см. SessionGrant. */
    requires_approval?: boolean
    approval_type?: string
  }> {
    const body = { email, code }
    // OTP-R3: с isCurrent эффекты ответа применяются только к актуальной попытке.
    const response = isCurrent
      ? await httpClient.guardedRequest<{ data: { token: string; location: string; requires_approval?: boolean; approval_type?: string } }>('/profile/login-by-code/verify', isCurrent, { method: 'POST', body: JSON.stringify(body) })
      : await httpClient.post<{ data: { token: string; location: string; requires_approval?: boolean; approval_type?: string } }>('/profile/login-by-code/verify', body)
    return response.data
  }

  /**
   * OTP-R6c — второй шаг входа: код из приложения-аутентификатора подтверждает
   * pending-ключ. Ключ уходит только сюда и только в заголовке этого запроса;
   * в хранилище входа он попадает из ответа, когда сервер вход подтвердил.
   * Неверный, повторный и сверх лимита код сервер отдаёт одной ошибкой.
   */
  static async confirmLoginTotp(pendingToken: string, code: string, isCurrent: () => boolean): Promise<void> {
    await httpClient.guardedRequest('/profile/login-approval', isCurrent, {
      method: 'POST',
      body: JSON.stringify({ code }),
      skipAuth: true,
      headers: { 'x-auth': pendingToken },
    })
  }

  /**
   * IGRA-106 раздел B — вход по magic-ссылке из письма об оплате.
   * Ответ той же формы, что у verifyLoginCode: сессия садится одним и тем же
   * кодом (см. auth/utils/sessionHandoff.ts).
   *
   * Любая невалидность (нет такого email, битый/просроченный/использованный
   * токен) приходит одной ошибкой без деталей — различать их фронту нечем и
   * незачем: наверх уходит MAGIC_LINK_INVALID, экран уводит человека в
   * обычный вход по коду.
   */
  static async loginByMagic(email: string, token: string, redirect = '', isCurrent?: () => boolean): Promise<SessionGrant> {
    // redirect отдаём только непустым: ручка валидирует его сама
    // (AuthController::resolveRedirectUrl — свой домен и непустой path),
    // но слать пустую строку в теле незачем.
    const payload = redirect ? { email, token, redirect } : { email, token }
    type MagicResponse = { data?: { token?: string; location?: string; requires_approval?: boolean; approval_type?: string } }
    const response = isCurrent
      ? await httpClient.guardedRequest<MagicResponse>('/profile/login-by-magic', isCurrent, { method: 'POST', body: JSON.stringify(payload) })
      : await httpClient.post<MagicResponse>('/profile/login-by-magic', payload)
    const data = response.data
    // Без токена входить некуда: пустое значение молча положило бы человека в
    // состояние «авторизован, но каждый запрос — 401».
    if (!data?.token) {
      throw new Error(MAGIC_LINK_INVALID)
    }

    // location отдаётся как есть: пустой обязан доехать до applySessionGrant,
    // у которого один фолбэк на весь вход — кабинет, а не публичный лендинг.
    // OTP-R6c: признак pending-ключа не теряем — иначе его посадили бы как вход.
    if (data.requires_approval) {
      return { token: data.token, location: data.location, requires_approval: true, approval_type: data.approval_type }
    }

    return { token: data.token, location: data.location }
  }

  /**
   * Start password recovery process
   * Returns a captcha session token
   */
  static async startPasswordRecovery(
    email: string,
    options?: { captchaSession?: string; recaptchaToken?: string; channel?: OtpChoiceChannel }
  ): Promise<RecoveryCodeResponse> {
    const payload: Record<string, string> = { email }
    if (options?.captchaSession) {
      payload['captcha-session'] = options.captchaSession
    } else if (options?.recaptchaToken) {
      payload[getCaptchaFieldName(options.recaptchaToken)] = options.recaptchaToken
    }
    if (options?.channel) payload.channel = options.channel
    const response = await httpClient.post<{ data: RecoveryCodeResponse }>('/profile/recovery-pass', payload)
    return response.data
  }

  /**
   * Verify recovery code
   */
  static async verifyRecoveryCode(email: string, code: string): Promise<{ token: string }> {
    const response = await httpClient.post<{ data: { token: string } }>('/profile/recovery-pass/check', { email, code })
    return response.data
  }

  /**
   * Set new password using recovery token
   */
  static async setNewPassword(data: {
    passwordnew: string
    passwordretry: string
    code: string
    token: string
    captchaSession?: string
    recaptchaToken?: string
  }): Promise<void> {
    const payload: Record<string, string> = {
      passwordnew: data.passwordnew,
      passwordretry: data.passwordretry,
      code: data.code
    }
    if (data.captchaSession) {
      payload['captcha-session'] = data.captchaSession
    } else if (data.recaptchaToken) {
      payload[getCaptchaFieldName(data.recaptchaToken)] = data.recaptchaToken
    }

    await httpClient.post('/profile/recovery-pass/new', payload, {
      headers: {
        'x-auth': data.token
      }
    })
  }
}
